import axios from "axios";
import { API_SERVER } from "./config";

export const API_BASE = process.env.REACT_APP_API_BASE_URL || `${API_SERVER}/api`;
export const SERVER_URL = API_BASE.replace("/api", "");

/* ---------------------------------------------------
    SAFE AXIOS INSTANCE
-----------------------------------------------------*/
const createAxiosInstance = (token) => {
  return axios.create({
    baseURL: API_BASE,
    headers: token && token.trim() !== ""
      ? { Authorization: `Bearer ${token}` }
      : {},   // 🔥 Prevents "Bearer null" → stops 403 errors
  });
};

/* ---------------------------------------------------
    AUTH APIs
-----------------------------------------------------*/

export const loginApi = async (email, password) => {
  const axiosInstance = createAxiosInstance();
  // ❌ Don't use "/auth/login"
  // ✅ Use "auth/login"
  const res = await axiosInstance.post("auth/login", { email, password }); 
  return res.data;
};

// Do the same for OTP
export const sendOtpApi = async (phone) => {
  const axiosInstance = createAxiosInstance(); 
  const res = await axiosInstance.post("auth/send-otp", { phone });
  return res.data;
};

// Verify OTP & Register (Handles Multipart for Profile Image)
export const verifyOtpApi = async (formData) => {
  const axiosInstance = createAxiosInstance();
  const res = await axiosInstance.post("/auth/verify-otp", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// 🚨 NEW: Check if User Exists before proceeding to OTP
export const checkUserExists = async (email, phone) => {
  try {
    const axiosInstance = createAxiosInstance();
    
    const res = await axiosInstance.post("/auth/check-user", { 
      email: email.trim().toLowerCase(), 
      phone: phone.trim() 
    });
    
    return res.data; 
    
  } catch (error) {
    // Grab the specific message from backend ("Email already in use", etc.)
    const errorMsg = error.response?.data?.message || "Verification failed. Try again.";
    throw new Error(errorMsg); 
  }
};

// ✅ Use this name in your api.js
export const registerApi = async (userData, imageFile) => {
  const formData = new FormData();
  
  // Add all text data to formData
  Object.keys(userData).forEach((key) => {
    formData.append(key, userData[key]);
  });
  
  // Add image if it exists
  if (imageFile) {
    formData.append("image", imageFile);
  }

  const axiosInstance = createAxiosInstance();
  // Ensure this matches your Node.js route (e.g., /auth/verify-otp or /auth/register)
  const res = await axiosInstance.post("/auth/verify-otp", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return res.data;
};

/* ---------------------------------------------------
    USER APIs
-----------------------------------------------------*/

// ✅ Add to utils/api.js
export const postRide = async (payload, token) => {
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.post("/rides", payload);
  return res.data;
};

// Fetch user profile
export const fetchUserProfile = async (userId, token) => {
  if (!userId) throw new Error("Missing user ID");
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.get(`/users/${userId}`);
  return res.data;
};

/* ---------------------------------------------------
    CHAT APIs
-----------------------------------------------------*/

// Fetch chat history by room
export const fetchChatHistory = async (roomId, token) => {
  if (!roomId) throw new Error("Missing room ID");
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.get(`/chat/history/${roomId}`);
  return res.data.messages || [];
};

// Fetch chat with specific user
export const fetchUserChat = async (otherUserId, token) => {
  if (!otherUserId) throw new Error("Missing other user ID");
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.get(`/chat/${otherUserId}`);
  return res.data.messages || [];
};

// Fetch all recent chats
export const fetchRecentConversations = async (token) => {
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.get(`/chat`);
  return res.data.conversations || [];
};

// Log call event
export const sendCallEvent = async ({ from, to, status, callType, duration = 0, token }) => {
  if (!from || !to || !status || !callType) {
    throw new Error("Missing required call event parameters");
  }
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.post("/chat/call-event", {
    from,
    to,
    status,
    callType,
    duration,
  });
  return res.data.messages || [];
};

/* ---------------------------------------------------
    NOTIFICATIONS
-----------------------------------------------------*/

export const fetchNotifications = async (userId, token) => {
  if (!userId) throw new Error("Missing user ID");
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.get(`/notifications/${userId}`);
  return res.data.data || [];
};

export const markMessagesAsSeen = async (messageIds, token) => {
  if (!Array.isArray(messageIds) || messageIds.length === 0)
    throw new Error("Missing or invalid message IDs");

  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.put(`/notifications/mark-seen`, { messageIds });
  return res.data;
};

/* ---------------------------------------------------
    RIDE REQUEST SYSTEM
-----------------------------------------------------*/

// ✅ Passenger: Request a seat
export const requestSeat = async (rideId, seatsRequested = 1, token, searchFrom, searchTo) => {
  if (!rideId) throw new Error("Missing rideId");

  const axiosInstance = createAxiosInstance(token);
  
  // Clean the payload
  const payload = { seatsRequested };
  
  // Only attach the flexible route data if it actually exists!
  if (searchFrom && searchTo && searchFrom.trim() !== "" && searchTo.trim() !== "") {
    payload.searchFrom = searchFrom;
    payload.searchTo = searchTo;
  }

  const res = await axiosInstance.post(`/rides/${rideId}/request`, payload);

  return res.data.request;
};

// Driver: Fetch all requests for a ride
export const fetchRideRequests = async (rideId, token) => {
  if (!rideId) throw new Error("Missing rideId");

  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.get(`/rides/${rideId}/requests`);
  return res.data.requests || [];
};

// Driver: Accept/Reject a seat request
export const updateRideRequest = async (rideId, requestId, action, token) => {
  if (!rideId || !requestId || !action)
    throw new Error("Missing required parameters");

  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.put(
    `/rides/${rideId}/requests/${requestId}`,
    { action }
  );

  // ✅ Return both request and ride
  return { request: res.data.request, ride: res.data.ride };
};

export const cancelRideRequest = async (rideId, requestId, token, reason) => {
  // ✅ FIX: Use axiosInstance to ensure headers/IP are handled correctly
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.put(`/rides/${rideId}/requests/${requestId}/cancel`, { 
    reason: reason 
  });
  return res.data;
};
// Passenger: Fetch their own requests
export const fetchMyRequests = async (token) => {
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.get(`/rides/my-requests`);
  return res.data.requests || [];
};


export const fetchRideById = async (rideId, token) => {
  if (!rideId) throw new Error("Missing rideId");
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.get(`/rides/${rideId}`);
  return res.data.ride;
};

// Cancel entire ride (driver or passenger)
export const cancelRide = async (rideId, { reason }, token) => {
  if (!rideId) throw new Error("Missing rideId");
  if (!reason) throw new Error("Missing cancellation reason");

  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.put(`/rides/${rideId}/cancel`, { reason });
  return res.data.ride;
};

// Driver: Mark ride as completed
export const completeRide = async (rideId, token) => {
  if (!rideId) throw new Error("Missing rideId");

  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.put(`/rides/${rideId}/complete`);
  return res.data.ride;
};

export default function RideDetailsCard({ ride, user, onRideUpdate }) {
  // ...

  const handleAction = async (requestId, action) => {
    try {
      const { request, ride: updatedRide } = await updateRideRequest(
        ride._id,
        requestId,
        action,
        token
      );

      setRequests((prev) => prev.map((r) => (r._id === request._id ? request : r)));

      // 🔥 Update ride in parent
      if (onRideUpdate) {
        onRideUpdate(updatedRide);
      }

      alert(action === "accept" ? "Seat confirmed ✅" : "Request rejected ❌");
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update request");
    }
  };

  // ...
}


// ✅ GET DRIVER'S REVIEWS AND RATING
export const getDriverReviews = async (driverId, token) => {
  if (!driverId) throw new Error("Missing driverId");
  try {
    // backend in your server is mounted such that the rating route is reachable at:
    // GET /api/rides/driver/:driverId/reviews
    const axiosInstance = createAxiosInstance(token);
    const res = await axiosInstance.get(`/rides/driver/${driverId}/reviews`);
    return res.data;
  } catch (error) {
    // return neutral object and log — caller must handle absence of reviews
    console.error("Error fetching reviews:", error?.message || error);
    return { driver: null };
  }
};

// ✅ SUBMIT RIDE RATING
export const submitRideRating = async (rideId, payload, token) => {
  const axiosInstance = createAxiosInstance(token);
  // ✅ FIX: Make sure this says "/rides/" and not "/rating/"
  const res = await axiosInstance.post(`/rides/${rideId}/rate`, payload);
  return res.data;
};

// Driver: Fetch pending requests for all their rides
export const fetchDriverPendingRequests = async (driverId, token) => {
  if (!driverId) throw new Error("Missing driverId");
  const axiosInstance = createAxiosInstance(token);
  // Using the centralized API_BASE automatically
  const res = await axiosInstance.get(`/rides/driver/${driverId}/pending-requests`);
  return res.data.requests || [];
};

// Add this to your API utility file
export const updateRide = async (rideId, rideData, token) => {
  if (!rideId) throw new Error("Missing rideId");
  
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.put(`/rides/${rideId}/edit`, rideData);
  return res.data;
};

// ✅ FETCH USER'S POSTED RIDES
export const fetchMyPostedRides = async (userId, token) => {
  if (!userId) throw new Error("Missing user ID");
  
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.get(`/rides/my-posted`, {
    params: { userId },
  });

  // Handle response structure & filter out nulls/undefined here
  const list = Array.isArray(res.data) ? res.data : res.data.rides || [];
  return list.filter((ride) => ride !== null && ride !== undefined);
};

export const searchRides = async (filters) => {
  const axiosInstance = createAxiosInstance(); // No token required for searching
  const res = await axiosInstance.get("/rides", {
    params: {
      from: filters.from,
      to: filters.to,
      date: filters.date || "",
      passengers: filters.passengers,
      type: "exact", // Request exact matches from backend
    },
  });
  // The backend returns an array of rides
  return Array.isArray(res.data) ? res.data : res.data.rides || [];
};

export const fetchSuggestedRides = async (filters) => {
  const axiosInstance = createAxiosInstance(); // No token needed for search
  const res = await axiosInstance.get("/rides", {
    params: {
      from: filters.from,
      to: filters.to,
      radius: filters.radius || 200, // Search within this range
      type: "flexible",              // Tell backend this is a suggestion search
    },
  });
  return Array.isArray(res.data) ? res.data : res.data.rides || [];
};

// ✅ Add this to your utils/api.js
export const updateProfileApi = async (formData, token) => {
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.put("/users/profile", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data; // Should return { success: true, user: {...} }
};

// ✅ Add this to your utils/api.js
export const getUnreadNotificationCount = async (userId, token) => {
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.get(`/notifications/${userId}`);
  // Filter for unseen notifications and return the count
  return res.data.data?.filter((n) => !n.seen).length || 0;
};

//faq and contact us page api
export const submitSupportQuery = async (payload) => {
  // 🚨 THE FIX: Make sure the URL ends with "/support/contact"
  // Assuming you are using axios and have API_BASE defined
  const res = await axios.post(`${API_BASE}/support/contact`, payload);
  return res.data;
};

// ✅ Send Email Verification OTP
export const sendEmailVerificationApi = async (userId, token) => {
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.post("/auth/send-email-otp", { userId });
  return res.data;
};

// ✅ Verify Email OTP
export const verifyEmailOtpApi = async (userId, otp, token) => {
  const axiosInstance = createAxiosInstance(token);
  const res = await axiosInstance.post("/auth/verify-email-otp", { userId, otp });
  return res.data;
};
